import { ApiError } from '~/api/errors';

/** True for a 409: the run moved on (gate decided elsewhere, run closed) since we last read it. */
export function isConflict(err: unknown): boolean {
  return err instanceof ApiError && err.statusCode === 409;
}

/**
 * True for a failure with no HTTP response (dropped connection, timeout) — the
 * only case a control action may resend, and then with the SAME correlationId.
 */
export function isNetworkFailure(err: unknown): boolean {
  if (err instanceof ApiError) return err.statusCode === null;
  return err instanceof TypeError || (err instanceof Error && err.name === 'TimeoutError');
}

/** Shape of a catalog 422's `details.errors[]` entry. */
interface FieldProblem {
  field?: unknown;
  message?: unknown;
}

/** The headline FastAPI puts on every 422 — the field lines under it are what says what's wrong. */
const GENERIC_VALIDATION_MESSAGE = 'Request validation failed';

/** What pydantic puts before a validator's own text (an older catalog-service still sends it). */
const PYDANTIC_PREFIXES = ['Value error, ', 'Assertion failed, '];

/** Where FastAPI says the field came from — not part of the field's name. */
const LOCATION_ROOTS = new Set(['body', 'path', 'query', 'header']);

/** One path segment as words: `maxProducts` / `schedule_id` → `Max products` / `Schedule id`. */
function segmentLabel(segment: string): string {
  const words = segment
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A 422's dotted field as a label: `trigger.maxProducts` → `Trigger › Max products`;
 *  list indices and the location root (`body`, `path`, …) are dropped. */
export function fieldLabel(field: string): string {
  return field
    .split('.')
    .filter(
      (part, i) => part !== '' && !/^\d+$/.test(part) && !(i === 0 && LOCATION_ROOTS.has(part)),
    )
    .map(segmentLabel)
    .join(' › ');
}

/** The validator's sentence without pydantic's `Value error, ` wrapper. */
function withoutPydanticPrefix(message: string): string {
  const prefix = PYDANTIC_PREFIXES.find((p) => message.startsWith(p));
  return prefix ? message.slice(prefix.length) : message;
}

/** Pulls `details.errors[]` out of a catalog error body without trusting its shape. */
function fieldProblems(body: unknown): string[] {
  if (typeof body !== 'object' || body === null) return [];
  const details = (body as { details?: unknown }).details;
  if (typeof details !== 'object' || details === null) return [];
  const errors = (details as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return [];
  return errors
    .map((entry: FieldProblem) => {
      const message = typeof entry.message === 'string' ? withoutPydanticPrefix(entry.message) : '';
      const label = typeof entry.field === 'string' ? fieldLabel(entry.field) : '';
      if (!message) return '';
      return label ? `${label}: ${message}` : message;
    })
    .filter((line) => line !== '');
}

/**
 * The ONE formatter for a failed catalog control call (gates, approval, recovery, schedules):
 * the server's message — the catalog's 409/422 messages are the instruction (e.g. "use FULL",
 * "lane busy") — plus any 422 field problems. Anything that is not an API error (dropped
 * connection, client bug) gets the plain `fallback` sentence, never raw exception text.
 */
export function controlErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const problems = fieldProblems(err.body);
  // A 422's generic headline adds nothing above the field lines that say what to fix.
  const headline =
    problems.length > 0 && err.message === GENERIC_VALIDATION_MESSAGE ? '' : err.message;
  const lines = [headline, ...problems].filter((l) => l.trim() !== '');
  return lines.length > 0 ? Array.from(new Set(lines)).join('\n') : fallback;
}
